import React from 'react';
import { useIntl, defineMessage } from 'react-intl';
import { Table } from 'reactstrap';
import { faListMusic } from '@fortawesome/pro-light-svg-icons/faListMusic';
import { TypedMemo } from '../../../common/types';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLLink } from '../SMLLink/SMLLink';
import { LoadingView } from '../../../common/views/LoadingView';
import { SMLImage } from '../SMLImage';
import { SMLEmptyState } from '../SMLEmptyState/SMLEmptyState';
import { IntegrationTypeIcon } from '../IntegrationTypeIcon';
import { PlaylistImageMosaic } from '../PlaylistImageMosaic/PlaylistImageMosaic';
import { MiniScoreChart } from '../MiniScoreChart/MiniScoreChart';
import styles from './PlaylistsTable.module.scss';

export interface PlaylistBasicData {
  id: string;
  name: string;
  imageUrl?: string | null;
  trackImageUrls?: string[];
  followersCount: number;
  integrationType: string;
}

export interface PlaylistsTableProps {
  playlists: PlaylistBasicData[];
  isLoading?: boolean;
  selectedDateValue?: string;
}

const messages = {
  playlistName: defineMessage({
    id: 'app.sml.playlists.table.playlistName',
    defaultMessage: 'Playlist Name',
  }),
  followers: defineMessage({
    id: 'app.sml.playlists.table.followers',
    defaultMessage: 'Followers',
  }),
  platform: defineMessage({
    id: 'app.sml.playlists.table.platform',
    defaultMessage: 'Platform',
  }),
  trend: defineMessage({
    id: 'app.sml.playlists.table.trend',
    defaultMessage: 'Trend',
  }),
  actions: defineMessage({
    id: 'app.sml.playlists.table.actions',
    defaultMessage: 'Actions',
  }),
  viewDetails: defineMessage({
    id: 'app.sml.playlists.table.viewDetails',
    defaultMessage: 'View Details',
  }),
};

export const PLAYLISTS_EXPORT_FILENAME = 'playlists';

const formatFollowersCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(0)}K`;
  }
  return count.toLocaleString('en-US');
};

const renderPlaceholderImage = (): React.ReactNode => (
  <div className={styles.placeholderImage}>
    <div className={styles.placeholderSquare} />
  </div>
);

interface PlaylistRowProps {
  playlist: PlaylistBasicData;
  selectedDateValue?: string;
}

const PlaylistRowComponent: React.FC<PlaylistRowProps> = ({ playlist, selectedDateValue }) => {
  const intl = useIntl();

  return (
    <tr>
      <td className={styles.dataCell}>
        <div className={styles.playlistInfo}>
          {playlist.trackImageUrls !== undefined && playlist.trackImageUrls.length > 0 ? (
            <PlaylistImageMosaic
              trackImageUrls={playlist.trackImageUrls}
              fallbackImageUrl={playlist.imageUrl ?? null}
              name={playlist.name}
              size={28}
            />
          ) : playlist.imageUrl ? (
            <SMLImage
              src={playlist.imageUrl}
              alt={playlist.name}
              width={28}
              makeSquareFit
              className={styles.playlistImage}
            />
          ) : (
            renderPlaceholderImage()
          )}
          <SMLLink
            to={SMLRoutes.playlistDetails}
            params={{ playlistId: playlist.id, ...(selectedDateValue !== undefined && { date: selectedDateValue }) }}
            className={styles.playlistName}
          >
            {playlist.name}
          </SMLLink>
        </div>
      </td>

      <td className={styles.dataCell}>
        <span className={styles.followersCount}>{formatFollowersCount(playlist.followersCount)}</span>
      </td>

      <td className={styles.dataCell}>
        <IntegrationTypeIcon integrationType={playlist.integrationType} size={36} />
      </td>

      <td className={styles.dataCell}>
        <MiniScoreChart entityType="playlist" entityId={playlist.id} />
      </td>

      <td className={styles.dataCellAction}>
        <SMLLink
          to={SMLRoutes.playlistDetails}
          params={{ playlistId: playlist.id, ...(selectedDateValue !== undefined && { date: selectedDateValue }) }}
          className="btn btn-primary btn-sm"
        >
          {intl.formatMessage(messages.viewDetails)}
        </SMLLink>
      </td>
    </tr>
  );
};

const PlaylistRow = TypedMemo(PlaylistRowComponent);

const PlaylistsTableComponent: React.FC<PlaylistsTableProps> = ({
  playlists,
  isLoading = false,
  selectedDateValue,
}) => {
  const intl = useIntl();

  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <LoadingView />
      </div>
    );
  }

  if (playlists.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <SMLEmptyState
          icon={faListMusic}
          title={intl.formatMessage({
            id: 'app.sml.playlists.table.empty.title',
            defaultMessage: 'No playlists yet',
          })}
          description={intl.formatMessage({
            id: 'app.sml.playlists.table.empty.description',
            defaultMessage: 'Follow some playlists to see them here.',
          })}
        />
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <Table responsive hover className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerCell}>{intl.formatMessage(messages.playlistName)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.followers)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.platform)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.trend)}</th>
            <th className={styles.headerCellAction}>{intl.formatMessage(messages.actions)}</th>
          </tr>
        </thead>
        <tbody>
          {playlists.map((playlist) => (
            <PlaylistRow key={playlist.id} playlist={playlist} selectedDateValue={selectedDateValue} />
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export const PlaylistsTable = TypedMemo(PlaylistsTableComponent);
